import { getRankChange, getScoreChange } from "./scorecard";
import { getEndDate, getPriorPeriodEndDate, getPriorPeriodStartDate, getStartDate } from "./time";
import { arrayStringToArray } from "./string";

export interface AiCommunicationDisposition {
    disposition_description?: string;
    id?: number;
    notes?: string;
}

interface AiCommunicationDispositionFields {
    dispositionDescription: string;
    id: string;
    notes: string;
}

const defaultAiCommunicationDispositionFields: AiCommunicationDispositionFields = {
    dispositionDescription: "ai_communication_disposition_description",
    id: "ai_communication_disposition_id",
    notes: "ai_communication_disposition_notes",
};

export function buildAiCommunicationDispositions(
    source: any,
    fields: AiCommunicationDispositionFields = defaultAiCommunicationDispositionFields,
): AiCommunicationDisposition[] {
    if (!(source?.[fields.id] > 0)) {
        return [];
    }

    return [
        {
            disposition_description: source[fields.dispositionDescription],
            id: source[fields.id],
            notes: source[fields.notes],
        },
    ];
}

export function approvedPercentage(stat: any): number {
    if (!stat || !(stat?.total_assessed > 0)) {
        return 0;
    }

    return Math.round(((stat.total_approved / stat.total_assessed) * 100) * 100) / 100;
}

export function approvedPercentageDifference(stat: any): number {
    if (!stat || !(stat?.total_assessed > 0) || !(stat?.total_assessed_prior_period > 0)) {
        return 0;
    }

    const totalApproved = approvedPercentage(stat);
    const totalApprovedPriorPeriod = approvedPercentagePriorPeriod(stat);
    return getScoreChange(totalApproved, totalApprovedPriorPeriod);
}

export function approvedPercentagePriorPeriod(stat: any): number {
    if (!stat || !(stat?.total_assessed_prior_period > 0)) {
        return 0;
    }

    return Math.round(((stat.total_approved_prior_period / stat.total_assessed_prior_period) * 100) * 100) / 100;
}

export function getPulseFilterCatalogOwnables(accountId: number, accountName: string): any[] {
    return [
        { name: 'Volie', value: 0, },
        { name: accountName, value: accountId || 0, },
    ];
}

export function getPulseFilterDateRangeTemplate(defaultValue: string): any {
    return {
        default: defaultValue,
        items: [
            { name: 'Current Week', value: 'current_week', },
            { name: 'Last 7 Days', value: 'last_7_days', },
            { name: 'Last 30 Days', value: 'last_30_days', },
            { name: 'Last 90 Days', value: 'last_90_days', },
            { name: 'Last 180 Days', value: 'last_180_days', },
            { name: 'Last 365 Days', value: 'last_365_days', },
            { name: 'Month To Date', value: 'MTD', },
            { name: 'Quarter To Date', value: 'QTD', },
            { name: 'Year To Date', value: 'YTD', },
        ],
    }
}

export function getPulseFilterReportType(defaultValue: string): any {
    return {
        default: defaultValue,
        items: [
            { name: 'Table', value: 'table', },
            { name: 'Line Chart', value: 'line_chart', },
        ],
    }
}

export function getPulseFilterView(defaultValue: string): any {
    return {
        default: defaultValue,
        items: [
            { name: 'Account', value: 'account', },
            { name: 'Agent', value: 'user', },
            { name: 'Assessment Item', value: 'llm_assessment_item_type', },
            { name: 'Campaign', value: 'campaign', },
            { name: 'Campaign Type', value: 'campaign_type', },
            { name: 'Employee', value: 'employee', },
            { name: 'Organization', value: 'organization', },
            { name: 'Team', value: 'team', },
        ],
    }
}

export function getDefaultlessValue(isEmptyObj: boolean): any {

    if (isEmptyObj) {
        return {};
    }

    return undefined;
}

export function getPulseFilters(searchObj: any, ownableId: number, isCatalogView: boolean): any {

    let filters = {
        campaigns: getDefaultlessValue(!isCatalogView),
        customerEscalated: getDefaultlessValue(!isCatalogView),
        dateRangeTemplate: getPulseFilterDateRangeTemplate(searchObj?.dateRangeTemplate || 'last_30_days'),
        includeTrackingLineCalls: searchObj?.includeTrackingLineCalls || '',
        organizations: getDefaultlessValue(!isCatalogView),
        appointmentOpportunityStatuses: getDefaultlessValue(!isCatalogView),
        llmAssessmentItemTypes: undefined,
        reportType: getPulseFilterReportType(searchObj?.reportType || 'table'),
        teams: getDefaultlessValue(!isCatalogView),
        users: getDefaultlessValue(!isCatalogView),
        view: getPulseFilterView(searchObj?.view || 'user'),
    }

    if (!isCatalogView || ownableId > 0) {
        filters.campaigns = {
            default: searchObj ? {
                campaignIds: searchObj.campaignIds,
                campaignName: searchObj.campaignName,
                campaignTypeIds: searchObj.campaignTypeIds,
                excludedCampaignName: searchObj.excludedCampaignName,
            } : undefined,
        }
        filters.organizations = {
            default: searchObj?.organizationIds || undefined,
        }
        filters.teams = {
            default: searchObj?.teamIds || undefined,
        }
        filters.users = {
            default: searchObj?.userIds || undefined,
        }
        filters.customerEscalated = {
            default: searchObj?.customerEscalated || null,
        }
        filters.appointmentOpportunityStatuses = {
            default: searchObj?.appointmentOpportunityStatuses || undefined,
            items: [
                { name: 'Open', value: 'open' },
                { name: 'Closed', value: 'closed' },
                { name: 'Missed', value: 'missed' },
                { name: 'Success', value: 'success' },
            ],
        }
    }

    if (filters.view?.default === 'llm_assessment_item_type') {
        filters.llmAssessmentItemTypes = {
            default: searchObj?.llmAssessmentItemTypeIds || undefined,
        }
    }

    return filters;
}

export function getSearchObj(searchParam: any) :any {

    return {
        campaignIds: searchParam?.campaign_ids || [],
        campaignName: searchParam?.campaign_name || null,
        campaignTypeIds: searchParam?.campaign_type_ids || [],
        customerEscalated: searchParam?.customer_escalated || null,
        dateRangeTemplate: searchParam?.date_range_template || null,
        endDate: getEndDate(searchParam.date_range_template).toISOString().split('T')[0],
        excludedCampaignName: searchParam?.excluded_campaign_name || null,
        includeTrackingLineCalls: searchParam?.include_tracking_line_calls || '',
        llmAssessmentItemTypeIds: searchParam?.llm_assessment_item_type_ids || [],
        organizationIds: searchParam?.organization_ids || [],
        appointmentOpportunityStatuses: parseAppointmentOpportunityStatuses(searchParam?.appointment_opportunity_statuses) || [],
        priorPeriodEndDate: getPriorPeriodEndDate(searchParam.date_range_template).toISOString().split('T')[0],
        priorPeriodStartDate: getPriorPeriodStartDate(searchParam.date_range_template).toISOString().split('T')[0],
        reportType: searchParam?.report_type || null,
        startDate: getStartDate(searchParam.date_range_template).toISOString().split('T')[0],
        teamIds: searchParam?.team_ids || [],
        userIds: searchParam?.user_ids || [],
        view: searchParam?.view || null,
    };
}

export function setSearchObjDatesFromDateRangeTemplate(searchObj: any): any {

    const newSearchObj = {...searchObj};
    
    newSearchObj.startDate = getStartDate(searchObj.dateRangeTemplate).toISOString().split('T')[0];
    newSearchObj.endDate = getEndDate(searchObj.dateRangeTemplate).toISOString().split('T')[0];
    newSearchObj.priorPeriodEndDate = getPriorPeriodEndDate(searchObj.dateRangeTemplate).toISOString().split('T')[0];
    newSearchObj.priorPeriodStartDate = getPriorPeriodStartDate(searchObj.dateRangeTemplate).toISOString().split('T')[0];

    return newSearchObj;
}

export function trendRankDifference(user: any, stats: any[]): number {
    if (!user || !(user?.total_assessed_prior_period > 0)) {
        return 0;
    }

    return getRankChange(user.rank, user.rank_prior_period, (stats || []).length)
}

// Find and Get endpoints return differing data - fix if we get a string
function parseAppointmentOpportunityStatuses(statuses: any): any {
    if (Array.isArray(statuses)) {
        return statuses;
    }

    if (typeof statuses === 'string') {
        return arrayStringToArray(statuses);
    }

    return [];
}